import React, { useCallback, useEffect, useRef } from 'react';
import { ApolloError } from '@apollo/client';
import { Box, Text } from 'rebass';
import { useHistory } from 'react-router-dom';
import type { ActivityFeedItem as ActivityFeedItemData } from './ActivityFeed.queries';
import ActivityFeedItem from './ActivityFeedItem';
import useNotificationActions from './useNotificationActions';
import { getVisibleActivityFeedItems } from './useActivityFeed';

type ActivityFeedListProps = {
  items: ActivityFeedItemData[];
  loading: boolean;
  error?: ApolloError;
  hasData: boolean;
  emptyMessage?: string;
  variant?: 'menu' | 'panel';
  maxHeight?: number | string;
  autoFocusFirstItem?: boolean;
  onRequestClose?: () => void;
  returnFocusRef?: React.RefObject<HTMLElement | null>;
};

const ActivityFeedList = ({
  items,
  loading,
  error,
  hasData,
  emptyMessage = 'No new activity.',
  variant = 'menu',
  maxHeight,
  autoFocusFirstItem = false,
  onRequestClose,
  returnFocusRef,
}: ActivityFeedListProps): JSX.Element => {
  const history = useHistory();
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const {
    markRead,
    markUnread,
    markAsFollowUp,
    unMarkAsFollowUp,
    dismiss,
  } = useNotificationActions();
  const visibleItems = getVisibleActivityFeedItems(items);

  const focusItem = useCallback((index: number) => {
    if (itemRefs.current.length === 0) {
      return;
    }
    const nextIndex =
      ((index % itemRefs.current.length) + itemRefs.current.length) %
      itemRefs.current.length;
    itemRefs.current[nextIndex]?.focus();
  }, []);

  const closeList = useCallback(() => {
    onRequestClose?.();
    returnFocusRef?.current?.focus();
  }, [onRequestClose, returnFocusRef]);

  const handleSelectItem = useCallback(
    async (notification: ActivityFeedItemData) => {
      if (typeof notification.actionPath !== 'string') {
        return;
      }

      if (!notification.read) {
        await markRead(notification.id);
      }

      if (variant === 'menu') {
        closeList();
      }
      history.push(notification.actionPath);
    },
    [closeList, history, markRead, variant]
  );

  const handleItemKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>, index: number) => {
      if (variant !== 'menu') {
        return;
      }

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          focusItem(index + 1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          focusItem(index - 1);
          break;
        case 'Home':
          event.preventDefault();
          focusItem(0);
          break;
        case 'End':
          event.preventDefault();
          focusItem(visibleItems.length - 1);
          break;
        case 'Escape':
          event.preventDefault();
          closeList();
          break;
        case 'Tab':
          closeList();
          break;
      }
    },
    [closeList, focusItem, visibleItems.length, variant]
  );

  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, visibleItems.length);
  }, [visibleItems.length]);

  useEffect(() => {
    if (
      !autoFocusFirstItem ||
      variant !== 'menu' ||
      visibleItems.length === 0
    ) {
      return;
    }
    const frameId = requestAnimationFrame(() => {
      focusItem(0);
    });
    return () => cancelAnimationFrame(frameId);
  }, [autoFocusFirstItem, focusItem, visibleItems.length, variant]);

  if (error) {
    return (
      <Box px={3} py={4}>
        <Text variant="body2" color={variant === 'menu' ? 'onNavbar' : 'text'}>
          Could not load activity. Please try again.
        </Text>
      </Box>
    );
  }

  if (loading && !hasData) {
    return (
      <Box px={3} py={4}>
        <Text variant="body2" color={variant === 'menu' ? 'onNavbar' : 'text'}>
          Loading...
        </Text>
      </Box>
    );
  }

  if (visibleItems.length === 0) {
    return (
      <Box px={3} py={4}>
        <Text variant="body2" color={variant === 'menu' ? 'onNavbar' : 'text'}>
          {emptyMessage}
        </Text>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        maxHeight,
        overflowY: maxHeight ? 'auto' : undefined,
      }}
    >
      {visibleItems.map((notification, index) => (
        <ActivityFeedItem
          key={notification.id}
          ref={(el) => {
            itemRefs.current[index] = el;
          }}
          notification={notification}
          index={index}
          onSelectItem={handleSelectItem}
          onMarkRead={markRead}
          onMarkUnread={markUnread}
          onMarkAsFollowUp={markAsFollowUp}
          onUnMarkAsFollowUp={unMarkAsFollowUp}
          onDismiss={dismiss}
          onKeyDown={handleItemKeyDown}
          role={variant === 'menu' ? 'menuitem' : 'button'}
          tabIndex={variant === 'menu' ? -1 : undefined}
          variant={variant}
        />
      ))}
    </Box>
  );
};

export default ActivityFeedList;
